/**
 * The greeting band at the top of the home page (#2640): a short wash with
 * the date, "Good morning, ‹name›", and at the right a decorative stage (three
 * characters, a desk-calendar page showing today, the plant and one speech
 * bubble with the total that waits across teams). The stage is `aria-hidden`;
 * the bubble hides on phones. The wash is the accent-soft token with a light
 * overlay from the card surface, so the dark themes still read.
 */
import { cn } from "../../lib/cn";
import { dateLine, greetingFor, monthName, thingsLine, weekdayName } from "./homeOverview";

const SPRITE = "/site/characters";
const RING_KEYS = ["r1", "r2", "r3", "r4", "r5", "r6", "r7"] as const;

export function GreetingBand({
  name,
  total,
  now,
}: {
  /** The viewer's display name; null while it loads. */
  name: string | null;
  /** Things that wait, summed over teams. */
  total: number;
  now: Date;
}) {
  const sprite = "absolute bottom-1 select-none";
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-border bg-accent-soft [background-image:radial-gradient(70%_140%_at_70%_30%,rgb(var(--color-bg-elevated)/0.5),transparent_70%)]"
      data-testid="home-greeting"
    >
      <div className="relative flex min-h-[200px] flex-col justify-center px-5 pb-0 pt-6 sm:px-10 sm:py-0">
        <div className="relative z-10 sm:pr-[380px] xl:pr-[600px]">
          <span className="mb-3.5 inline-block rounded-full border border-accent/30 px-3 py-1 text-xs uppercase tracking-[0.12em] text-accent-fg">
            {dateLine(now)}
          </span>
          <h1 className="text-[26px] font-medium leading-tight tracking-tight text-fg sm:text-[34px]">
            {name ? `${greetingFor(now.getHours())}, ${name}` : greetingFor(now.getHours())}
          </h1>
        </div>
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none relative mt-2 h-[120px] w-full overflow-hidden",
            "sm:absolute sm:inset-y-0 sm:right-6 sm:mt-0 sm:h-auto sm:w-[364px] sm:overflow-visible xl:right-10 xl:w-[560px]",
          )}
          data-testid="home-stage"
        >
          <div className="absolute bottom-0 left-0 h-[200px] w-[560px] origin-bottom-left scale-[0.6] sm:left-auto sm:right-0 sm:origin-bottom-right xl:inset-0 xl:left-0 xl:h-full xl:origin-bottom-left xl:scale-100">
            <div className="absolute inset-x-0 bottom-1.5 h-[18px] [background:radial-gradient(closest-side,rgb(var(--color-fg)/0.12),transparent)]" />
            <img
              src={`${SPRITE}/sam/celebrate.svg`}
              alt=""
              className={cn(sprite, "-left-2.5 h-40")}
            />
            <img
              src={`${SPRITE}/maya/phone.svg`}
              alt=""
              className={cn(sprite, "left-[110px] h-[172px]")}
            />
            <div className="absolute bottom-3.5 left-[250px] h-[132px] w-32 -rotate-3 rounded-[10px] bg-bg-elevated shadow-[0_3px_0_-1px_rgb(var(--color-border)),0_24px_40px_-22px_rgb(var(--color-fg)/0.5)]">
              <div className="absolute inset-x-3 -top-2 flex justify-between">
                {RING_KEYS.map((k) => (
                  <span
                    key={k}
                    className="h-4 w-[5px] rounded-sm border border-fg-faint bg-bg-subtle"
                  />
                ))}
              </div>
              <span className="absolute left-3.5 top-[18px] text-[13px] font-semibold text-accent-fg">
                {monthName(now)}
              </span>
              <span className="absolute left-3.5 top-9 text-[54px] font-medium leading-none tracking-tight text-fg">
                {now.getDate()}
              </span>
              <span className="absolute bottom-3.5 left-3.5 text-[13px] text-fg-subtle">
                {weekdayName(now)}
              </span>
            </div>
            <img
              src={`${SPRITE}/jo/point.svg`}
              alt=""
              className={cn(sprite, "left-[380px] h-[172px]")}
            />
            <img
              src={`${SPRITE}/props/plant.svg`}
              alt=""
              className="absolute bottom-2 left-[500px] h-[46px] select-none"
            />
            <span
              className="absolute left-[300px] top-5 hidden whitespace-nowrap rounded-xl bg-bg-elevated px-3 py-[7px] text-[13px] text-fg shadow-[0_6px_18px_-8px_rgb(var(--color-fg)/0.35)] xl:block"
              data-testid="home-bubble"
            >
              {thingsLine(total)}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
